import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import type { ValuationFormValues } from '../../../components/CarValuationPage/components/ValuationTool/ValuationTool.schema';
import { useHomePageContext } from '../../../contexts/HomePageContext';
import { convertMilesToKm } from '../../../helpers/formatting';
import { useBrowserStorage } from '../../../hooks/UseBrowserStorage';
import { useQueryParams } from '../../../hooks/UseQueryParams';
import { useUserContext } from '../../../contexts/UserContext';
import { MILEAGE_UNIT } from '../../../types/index';
import { useGetCarValuation } from '../../../features/landing/HomePageValuationLookup/queries/useGetCarValuation';
import { useGetVehicleLookup } from '../../../features/landing/HomePageValuationLookup/queries/useGetVehicleLookup';

import { HOMEPAGE_VALUATION_LOOKUP } from '../../../features/landing/HomePageValuationLookup/HomePageValuationLookup.typed';

const useHomePageValuationLookup = () => {
  const { setIsValuationModalOpen, setHomePageValuationModalData } =
    useHomePageContext();

  const { asPath } = useRouter();

  const { status, handleLogin } = useUserContext();
  const { checkIfQueryIsPresent } = useQueryParams();
  const { setLocalStorageItem, getLocalStorageItem, removeLocalStorageItem } =
    useBrowserStorage();

  const { mutateAsync: fetchCarValuation } = useGetCarValuation();
  const { mutateAsync: fetchVehicleLookup } = useGetVehicleLookup();

  const MODAL_QUERY_KEY = 'modal';
  const MODAL_QUERY_VALUE = 'homepage-valuation';
  const isHomePageValuationModalQueryPresent = checkIfQueryIsPresent(
    MODAL_QUERY_KEY,
    MODAL_QUERY_VALUE,
  );
  const callbackUrl = isHomePageValuationModalQueryPresent
    ? asPath
    : `${asPath}&modal=${MODAL_QUERY_VALUE}`;

  const openModal = () => setIsValuationModalOpen(true);

  const handleValuationLookup = async (
    registration: string,
    mileage: string,
  ) => {
    const [carValuation, vehicleLookup] = await Promise.all([
      fetchCarValuation({ registration, mileage }),
      fetchVehicleLookup(registration),
    ]);

    const attributes = vehicleLookup.metaInfoBlock.items;

    const make =
      attributes?.find((item) => item.name === 'make')?.displayValue || '';
    const model =
      attributes?.find((item) => item.name === 'model')?.displayValue || '';
    const year =
      attributes?.find((item) => item.name === 'year')?.displayValue || '';
    const bodyType =
      attributes?.find((item) => item.name === 'bodyType')?.displayValue || '';
    const fuelType =
      attributes?.find((item) => item.name === 'fuelType')?.displayValue || '';
    const transmission =
      attributes?.find((item) => item.name === 'transmission')?.displayValue ||
      '';
    const trim =
      vehicleLookup.hiddenFields.items?.find((item) => item.name === 'trim')
        ?.value || '';

    const { min, max } = carValuation.privateMarketOfferRange;

    return {
      make,
      model,
      year,
      trim,
      bodyType,
      fuelType,
      transmission,
      valuation: { min, max },
    };
  };

  const deleteValuationDataFromLocalStorage = () => {
    const valuationData: ValuationFormValues = getLocalStorageItem(
      HOMEPAGE_VALUATION_LOOKUP.KEY,
    );

    if (valuationData) {
      removeLocalStorageItem(HOMEPAGE_VALUATION_LOOKUP.KEY);
    }
  };

  const onSubmit = async (values: ValuationFormValues) => {
    const { registration, mileage, mileageType } = values;

    if (status === 'unauthenticated') {
      setLocalStorageItem(HOMEPAGE_VALUATION_LOOKUP.KEY, {
        registration,
        mileage,
        mileageType,
        expiryDate: (Date.now() + 12096e5).toString(),
      });
      handleLogin(callbackUrl);
      return;
    }

    try {
      const mileageValue =
        mileageType === MILEAGE_UNIT.MILES
          ? convertMilesToKm(Number(mileage))
          : mileage;

      const lookupValues = await handleValuationLookup(
        registration,
        mileageValue,
      );

      setHomePageValuationModalData({
        ...values,
        ...lookupValues,
      });

      openModal();
      deleteValuationDataFromLocalStorage();
    } catch {
      setHomePageValuationModalData(undefined);
      openModal();
    }
  };

  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  useEffect(() => {
    if (isHomePageValuationModalQueryPresent) {
      const valuationData: ValuationFormValues = getLocalStorageItem(
        HOMEPAGE_VALUATION_LOOKUP.KEY,
      );

      if (valuationData) {
        const { registration, mileage, mileageType } = valuationData;
        onSubmitRef.current({ registration, mileage, mileageType });
      }
    } else {
      setIsValuationModalOpen(false);
    }
  }, [
    isHomePageValuationModalQueryPresent,
    getLocalStorageItem,
    setIsValuationModalOpen,
    status,
  ]);

  return { onSubmit, isHomePageValuationModalQueryPresent };
};

export { useHomePageValuationLookup };
